// SCR-019/020, `/accounts` (legacy pages/shared/accounts/AccountsIndex.vue, AccountsList.vue, AccountCard.vue,
// composables/useAccountsLocationsStitch.ts; field note backoffice.accounts.list): where Radish staff and resellers land
// after signing in, in the staff shell (sidebar, toolbar title "Accounts") under one "Accounts" tab. The header has
// the search (accounts, locations, emails, phones), the Reseller filter (Radish admins only, legacy `loggedInAsAdmin`)
// and the Last drawer activity filter; then one card per account, sorted by name: its name, reseller, email and phone,
// and each location with its email, last drawer activity and Log in. Legacy's location status chip and filter are off
// in legacy (LOCATION_STATUS_UI_ENABLED = false), so they are not drawn. Radish admins and resellers have Create Account
// and the card menu's Edit account (DLG-128, AccountDialog.tsx) and Invite user (DLG-134, or DLG-135 No Location while
// the account has none; AccountInviteDialog.tsx).
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Building2, ChevronRight, Copy, Eye, Mail, Pencil, Phone } from 'lucide-react';
import { MenuItem, RowMenu } from '../users/UsersPage.tsx';
import type { AccountChoice } from '@radish/contracts';
import { Button, EmptyState, IconButton, LoadingState, SearchField, SelectField, TabBar, useToast } from '@radish/ui-web';
import { ListHeader } from '../../shell/page.tsx';
import { bearerToken, choosingAccount, sessionSnapshot, switchTo } from '../../session/session.ts';
import { useSignedIn } from '../../session/use-session.ts';
import { phoneText } from '../branding/LocationInfoPage.tsx';
import { StaffShell } from '../../shell/StaffShell.tsx';
import { AccountDialog } from './AccountDialog.tsx';
import { AccountInviteDialog, type InviteTarget } from './AccountInviteDialog.tsx';
import { activityTooltip, DRAWER_ACTIVITY_FILTERS, drawerActivity, type ActivityBucket, type ActivityVariant } from './activity.ts';

type Account = AccountChoice;
type Location = Account['locations'][number];

const has = (value: string | null | undefined, term: string) => value !== null && value !== undefined && value.toLowerCase().includes(term);
const accountMatches = (a: Account, term: string) => has(a.name, term) || has(a.email, term) || has(a.phone, term);
const locationMatches = (l: Location, term: string) => has(l.name, term) || has(l.email, term);

/**
 * Legacy's stitch: accounts by name; with a term or a band, an account keeps the locations that match (all of them
 * when the account itself matches the term) and is dropped when none is left, unless only the term is set and the
 * account matches it.
 */
function filterAccounts(accounts: readonly Account[], rawTerm: string, reseller: string | null, bucket: ActivityBucket | null, now: number): Account[] {
  const term = rawTerm.trim().toLowerCase();
  const sorted = accounts
    .filter((a) => reseller === null || a.resellerName === reseller)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  if (term === '' && bucket === null) return sorted;
  return sorted.flatMap((a) => {
    const hit = term !== '' && accountMatches(a, term);
    const locations = a.locations.filter((l) => (term === '' || hit || locationMatches(l, term)) && (bucket === null || drawerActivity(l.lastDrawerOpenedAt, now).bucket === bucket));
    return locations.length > 0 || (bucket === null && hit) ? [{ ...a, locations }] : [];
  });
}

/**
 * Legacy Chip variants (`Chip.vue`) as LocationActivityChip uses them: green and red-filled are solid, blue, orange and
 * gray are tinted with no visible border. Green takes the AA ink (#4d833d, #3c6a2f under AA) where legacy's #679f55
 * under white text falls short of 4.5:1, and orange keeps the warning ink for the same reason.
 */
const CHIP: Readonly<Record<ActivityVariant, string>> = {
  green: 'border-transparent bg-positive-ink text-text-inverted',
  blue: 'border-transparent bg-[var(--q-brand-blue-lighter)] text-[var(--q-brand-blue)]',
  red: 'border-transparent bg-negative text-white',
  orange: 'border-transparent bg-[var(--q-brand-orange-lighter)] text-warning',
  gray: 'border-transparent bg-[var(--q-surface-toggle-background)] text-text-2',
};

export function AccountsPage() {
  useSignedIn();
  const navigate = useNavigate();
  const toast = useToast();
  const view = choosingAccount() ?? sessionSnapshot()?.session ?? null;
  const [term, setTerm] = useState('');
  const [reseller, setReseller] = useState<string | null>(null);
  const [bucket, setBucket] = useState<ActivityBucket | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** The Create/Edit Account dialog: 'new', an account id, or closed. */
  const [dialog, setDialog] = useState<string | null>(null);
  /** DLG-134 / DLG-135: the account the card menu's Invite user was chosen on. */
  const [inviting, setInviting] = useState<InviteTarget | null>(null);
  const [now] = useState(() => Date.now());
  const searchBox = useRef<HTMLDivElement>(null);
  // Legacy's search is `autofocus`.
  useEffect(() => searchBox.current?.querySelector('input')?.focus(), [view]);
  const accounts = useMemo(() => view?.accounts ?? [], [view]);
  const rows = useMemo(() => filterAccounts(accounts, term, reseller, bucket, now), [accounts, term, reseller, bucket, now]);
  const resellers = useMemo(() => [...new Set(accounts.flatMap((a) => (a.resellerName === null || a.resellerName === undefined ? [] : [a.resellerName])))].sort(), [accounts]);
  if (view === null) return <LoadingState />;
  const isAdmin = view.user.staffRole === 'admin';
  // Radish admins and resellers create and edit accounts (legacy AccountsIndex); support and viewer staff do not.
  const canManage = isAdmin || (view.user.staffRole === null && (accounts.some((a) => a.via === 'reseller') || (view.current === null && bearerToken() !== null)));

  const logIn = async (account: Account, location: Location) => {
    setBusy(location.locationId);
    const result = await switchTo(account.accountId, location.locationId);
    setBusy(null);
    if (!result.ok) return toast.error(result.message);
    await navigate({ to: `/l/${location.slug}` });
  };
  const copy = (email: string) => {
    void navigator.clipboard.writeText(email).then(() => toast.success('Email copied to clipboard'), () => toast.error('Could not copy email'));
  };

  return (
    <StaffShell title="Accounts">
      <div data-testid="accounts-page" data-screen="backoffice.accounts.list">
        <TabBar
          label="Accounts tabs"
          items={[{ id: 'accounts', label: 'Accounts', active: true }]}
          renderTab={(item, className) => <Link to="/accounts" className={className} aria-current="page">{item.label}</Link>}
        />
        {/* Legacy AccountsList's PageHeaderAlternate (B1146): the search (`ui-header-input`, 180 px), Reseller (180) and Last
            drawer activity (`.activity-filter`, 240) 10 px apart, Create Account at the right; under 600 all stacked
            column-reverse, Create Account on top. `wrap` keeps Create Account on screen from 600 to 800 (B1200). */}
        <ListHeader
          wrap
          start={<>
            <div ref={searchBox} className="contents"><SearchField value={term} onValueChange={setTerm} wide /></div>
            {isAdmin ? (
              <SelectField label="Reseller" dense value={reseller} options={resellers.map((r) => ({ value: r, label: r }))} onValueChange={setReseller} onClear={() => setReseller(null)} className="min-[600px]:w-[180px]" />
            ) : null}
            <SelectField label="Last drawer activity" dense value={bucket} options={DRAWER_ACTIVITY_FILTERS} onValueChange={setBucket} onClear={() => setBucket(null)} className="min-[600px]:w-[240px]" />
          </>}
          end={canManage ? <Button onClick={() => setDialog('new')}>Create Account</Button> : null}
        />
        {accounts.length === 0 ? (
          <EmptyState uiNoDataPhone illustration="/assets/missing-states/light/missing_state_customers.svg" darkIllustration="/assets/missing-states/dark/missing_state_customers_dark.svg" title="You don’t have any accounts yet." />
        ) : rows.length === 0 ? (
          <p className="px-1 py-6 text-[14px] text-text-2">No accounts or locations match your search.</p>
        ) : (
          rows.map((a) => (
            <section key={a.accountId} className="mb-4 overflow-hidden rounded-[12px] border border-line bg-surface shadow-sm" data-testid="account-card">
              <div className="flex flex-wrap items-center gap-4 border-b border-line py-1 pr-2 pl-4">
                <span className="min-h-9 flex-1 content-center">
                  <Link to="/accounts/$accountId" params={{ accountId: a.accountId }} aria-label={`${a.name} account`} className="text-[13px] font-bold text-text-1 hover:text-primary" data-testid="account-card-title">{a.name}</Link>
                </span>
                <div className="flex flex-wrap items-center gap-3">
                  {a.resellerName === null || a.resellerName === undefined ? null : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[var(--q-brand-secondary-alternate)] px-[9px] py-[3px] text-[11px] leading-none font-semibold text-secondary" title="Reseller">
                      <Building2 size={12} aria-hidden />{a.resellerName}
                    </span>
                  )}
                  {a.email === null || a.email === undefined ? null : <span className="inline-flex items-center gap-1 text-[13px] text-text-2"><Mail size={14} aria-hidden />{a.email}</span>}
                  {a.phone === null || a.phone === undefined ? null : <span className="inline-flex items-center gap-1 text-[13px] text-text-2"><Phone size={14} aria-hidden />{phoneText(a.phone.replace(/\D/g, ''))}</span>}
                </div>
                {a.via === 'membership' ? null : (
                  <RowMenu label={`Actions for ${a.name} account`}>
                    <MenuItem onSelect={() => void navigate({ to: '/accounts/$accountId', params: { accountId: a.accountId } })}><Eye size={18} className="mr-3 text-text-2" aria-hidden />View account</MenuItem>
                    {canManage ? <MenuItem onSelect={() => setDialog(a.accountId)}><Pencil size={18} className="mr-3 text-text-2" aria-hidden />Edit account</MenuItem> : null}
                    {canManage ? (
                      <MenuItem onSelect={() => setInviting({ accountId: a.accountId, name: a.name, hasLocation: a.locations.length > 0 })}><Mail size={18} className="mr-3 text-text-2" aria-hidden />Invite user</MenuItem>
                    ) : null}
                  </RowMenu>
                )}
              </div>
              {a.locations.length === 0 ? (
                <p className="px-4 py-3 text-[14px] text-text-3">No locations yet</p>
              ) : (
                <ul>
                  {a.locations.map((l) => {
                    const activity = drawerActivity(l.lastDrawerOpenedAt, now);
                    return (
                      <li
                        key={l.locationId}
                        className={`flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3 last:border-b-0 hover:bg-page ${a.via === 'membership' ? '' : 'cursor-pointer'}`}
                        data-testid="account-location-row"
                        // Legacy: the row opens the location's staff page; Log in and the email's copy stop the click.
                        onClick={(e) => {
                          if (a.via === 'membership' || (e.target as HTMLElement).closest('button, a') !== null) return;
                          void navigate({ to: '/accounts/$accountId/location/$locationId', params: { accountId: a.accountId, locationId: l.locationId } });
                        }}
                      >
                        <div className="flex min-w-0 flex-col">
                          {a.via === 'membership' ? <span className="text-[16px] font-semibold text-text-1">{l.name}</span> : (
                            <Link to="/accounts/$accountId/location/$locationId" params={{ accountId: a.accountId, locationId: l.locationId }} className="text-[16px] font-semibold text-text-1 hover:text-primary">{l.name}</Link>
                          )}
                          {l.email === null || l.email === undefined || l.email === '' ? null : (
                            <span className="flex items-center gap-1 text-[12px] text-text-2">
                              {l.email}
                              <IconButton label="Copy email" onClick={() => copy(l.email ?? '')}><Copy size={14} aria-hidden /></IconButton>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`inline-flex items-center rounded-chip border px-2 py-0.5 text-[11px] font-semibold ${CHIP[activity.variant]}`} title={activityTooltip(l.lastDrawerOpenedAt, l.timezone)} data-testid="location-activity-chip">
                            {activity.label}
                          </span>
                          <button
                            type="button"
                            className="cursor-pointer text-[13px] font-semibold whitespace-nowrap text-secondary underline disabled:cursor-wait disabled:opacity-60"
                            disabled={busy === l.locationId}
                            onClick={() => void logIn(a, l)}
                            aria-label={`Log in to ${l.name}`}
                          >
                            Log in
                          </button>
                          <ChevronRight size={20} className="text-text-3" aria-hidden />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          ))
        )}
      </div>
      <AccountInviteDialog target={inviting} onClose={() => setInviting(null)} />
      <AccountDialog open={dialog !== null} accountId={dialog === 'new' ? null : dialog} isAdmin={isAdmin} onClose={() => setDialog(null)} />
    </StaffShell>
  );
}
